import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { map } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { tryLoadAccountUsers } from '@capital-access/account/users';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { LocalizationService } from '@capital-access/common/localization';
import { DEFAULT_TEMPLATE_KEY, LoadStatus, SECTIONS_LAYOUT_KEY } from '@capital-access/common/utils';
import { Breakpoint, ChipSize, FireflyDrawerService } from '@capital-access/firefly/components';
import { ProfileType } from '@capital-access/profiles/common';
import {
  createConfigurableProfileReport,
  EXPORT_REPORT_INCLUDED_CONTACTS,
  EXPORT_REPORT_MODULE_NAME,
  EXPORT_REPORT_SELECTED_PROFILES,
  removeLocalStorage,
  ReportType
} from '@capital-access/reporting/common';
import { BaseReportDrawer } from '../../../components/report-drawer/base-report-drawer';
import { saveReportDefaultTemplate, saveReportLayoutSettings } from '../../../core/+state/report.actions';
import { SectionSelectorListItem } from '../../../core/models/report-sections';
import { createProfileReport } from '../../+state/profile-report.actions';
import { CreateProfileReportDto } from '../../models/profile-report.models';
import { ProfileReportLayout } from '../../models/profile-report-sections';
import { TemplatesStore } from '../../services/templates.store';

@Component({
  templateUrl: './profile-report-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TemplatesStore]
})
export class ProfileReportDrawerComponent
  extends BaseReportDrawer<CreateProfileReportDto, Partial<Record<ProfileReportLayout, SectionSelectorListItem[]>>>
  implements OnInit
{
  layout: ProfileReportLayout = ProfileReportLayout.OnePager;
  filteredSections: Record<string, SectionSelectorListItem[]> = {};
  readonly templatesStore = inject(TemplatesStore);

  chipSize = ChipSize.Sm;
  ProfileReportLayout = ProfileReportLayout;

  ngOnInit() {
    // load template if dafault template is Configurable
    if (this.layout === ProfileReportLayout.Configurable) {
      this.loadConfigurableTemplates();
    }
  }

  layoutOptions$ = this.localizationService.getLocalization(this.localizationScope).pipe(
    withLatestFrom(this.store.select(getFeatureToggle('configurable-reporting-export-workflows'))),
    map(([localization, isConfigurableEnabled]) => [
      { text: localization['onePager'], id: ProfileReportLayout.OnePager },
      { text: localization['detailed'], id: ProfileReportLayout.Detailed },
      ...(!this.isMobile && isConfigurableEnabled
        ? [{ text: localization['configurable'], id: ProfileReportLayout.Configurable }]
        : [])
    ])
  );

  get settingsKey(): string {
    return this.layout === ProfileReportLayout.Detailed
      ? this.request!.profileType
      : `${this.request!.profileType}${this.layout}`;
  }

  override getSections(
    filteredSections: Record<ProfileReportLayout, SectionSelectorListItem[]>
  ): SectionSelectorListItem[] {
    return filteredSections[this.layout];
  }

  override onInjectInputs(inputs: {
    request?: CreateProfileReportDto;
    layout: ProfileReportLayout;
    filteredSections: Partial<Record<ProfileReportLayout, SectionSelectorListItem[]>>;
  }) {
    this.layout = inputs.layout;
    this.filteredSections = inputs.filteredSections;
    this.store.dispatch(tryLoadAccountUsers());
    super.onInjectInputs(inputs);
  }

  override saveSettings() {
    super.saveSettings();
    this.saveLayoutSetting();
    //remove default template start once default setting change to other than configuration-tempalte
    this.removeLocalStorage();
  }

  saveLayoutSetting() {
    this.store.dispatch(
      saveReportLayoutSettings({
        key: `${this.request!.profileType}_${SECTIONS_LAYOUT_KEY}`.toUpperCase(),
        layout: this.layout.toString()
      })
    );
  }

  removeLocalStorage() {
    this.store.dispatch(
      removeLocalStorage({
        keys: [`${this.request!.profileType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()]
      })
    );
  }

  saveDefaultTemplateSetting() {
    if (this.form.controls.setAsDefault.touched && this.form.controls.setAsDefault.value) {
      this.saveLayoutSetting();
      this.store.dispatch(
        saveReportDefaultTemplate({
          key: `${this.request!.profileType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase(),
          defaultTemplateId: this.selectedTemplateId?.toString() ?? null
        })
      );
    }
  }

  setLayout($event: unknown) {
    this.layout = ($event as { id: ProfileReportLayout }).id;
    if (this.layout === ProfileReportLayout.Configurable) {
      this.loadConfigurableTemplates();
      this.switchOffSetAsDefault();
    } else {
      this.form.controls.sections.patchValue(this.getSections(this.filteredSections));
      this.settingsChange(this.form.controls.sections.value);
      this.drawerService.changeDrawerFooterVisibility(false);
    }
  }

  get createReportAction(): Action {
    return createProfileReport({ request: this.request!, sections: this.getSelectedSection, layout: this.layout });
  }

  override createReport() {
    if (this.layout === ProfileReportLayout.Configurable) {
      this.saveDefaultTemplateSetting();
      this.store.dispatch(
        createConfigurableProfileReport({
          profiles: this.request!.profiles.map(p => ({ prId: p.id.prId!, crmId: p.id.crmId!, name: p.name ?? '' })),
          profileType: this.request!.profileType === ProfileType.Institution ? 'institution' : 'contact',
          templateId: this.selectedTemplateId!,
          moduleName: this.request?.moduleName,
          includedContacts: this.request?.includedContacts ?? [],
          reportedQuartersFilter: (this.request?.profiles || []).map(p => {
            return {
              profileId: `${p.id.prId ?? ''}|${p.id.crmId ?? ''}`,
              reportedQuarters: this.request!.reportedQuarters ?? []
            };
          })
        })
      );
      this.drawerInstanceRef.setConfirmationModalTrigger(false);
      this.close({ reportInProgress: true });
    } else super.createReport();
  }

  loadConfigurableTemplates() {
    const reportType =
      this.request!.profileType === ProfileType.Institution ? ReportType.Institution : ReportType.Contact;
    this.templatesStore.loadTemplates(reportType);
  }

  previewAndCustomize() {
    this.saveDefaultTemplateSetting();
    if (this.request?.moduleName) {
      localStorage.setItem(EXPORT_REPORT_MODULE_NAME, this.request?.moduleName);
    }

    if (this.request?.profiles?.length) {
      const profiles = this.request!.profiles?.map(p => ({
        prId: p.id.prId,
        crmId: p.id.crmId,
        name: p.name ?? '',
        profileType: this.request!.profileType,
        reportedQuarters: this.request!.reportedQuarters ?? []
      }));
      localStorage.setItem(EXPORT_REPORT_SELECTED_PROFILES, JSON.stringify(profiles));
    }

    if (this.request?.includedContacts?.length) {
      localStorage.setItem(EXPORT_REPORT_INCLUDED_CONTACTS, JSON.stringify(this.request?.includedContacts));
    }
    if (this.form.controls.setAsDefault.touched && this.form.controls.setAsDefault.value) {
      this.templatesStore.setDefaultTemplate(this.templatesStore.templates(), this.selectedTemplateId ?? 0);
      this.form.markAsPristine();
    }
  }

  get selectedTemplateId(): number | undefined {
    return this.templatesStore.selectedTemplate()?.templateId;
  }

  get isMobile() {
    return window.innerWidth < Breakpoint.Sm;
  }

  constructor(store: Store, drawerService: FireflyDrawerService, private localizationService: LocalizationService) {
    super(store, drawerService);
    effect(() => {
      const isLoading = this.templatesStore.loadStatus() === LoadStatus.Loading;
      const noData = !this.templatesStore.templates().length;
      const shouldHide = (isLoading || noData) && this.layout === ProfileReportLayout.Configurable;
      drawerService.changeDrawerFooterVisibility(shouldHide);
    });
  }
}
